import { Helmet } from "react-helmet-async";

const SITE_NAME = "일광오피스";
const BASE_URL = "https://ikoffices.com";

const defaults = {
  title: "복사기 복합기 렌탈·판매 전문 | 일광오피스 - 가격비교·토너·무상유지보수",
  description:
    "복사기, 복합기 전문 일광오피스. 사무용 복사기 렌탈 월 3만원부터, 컬러·흑백 복합기 판매, 토너 소모품 최저가 비교. 삼성·캐논·후지필름·교세라·신도리코·HP 복사기 AI 맞춤 추천. 전국 무상 출장 유지보수.",
  keywords:
    "복사기, 복합기, 복사기 렌탈, 복합기 렌탈, 사무용 복사기, 복사기 가격, 복합기 가격, 복사기 추천, 복합기 추천, 복사기 판매, 사무용 복합기, 컬러 복사기, 흑백 복사기, 토너, 프린터, 복사기 렌탈 가격, 복합기 렌탈 가격, 복사기 유지보수, 사무실 복사기, 일광오피스",
};

const toAbsoluteUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
};

export default function SEO({ title, description, keywords, path = "", noindex = false, product = null, breadcrumbs = null, areaServed = "" }) {
  const pageTitle = title || defaults.title;
  const pageDesc = description || defaults.description;
  const pageKeywords = keywords || defaults.keywords;
  const canonical = `${BASE_URL}${path}`;
  // og:image 는 절대 URL 이어야 링크 미리보기/크롤러가 읽는다 (hero_image 는 보통 "/api/..." 상대경로)
  if (product) product = { ...product, image: toAbsoluteUrl(product.image) };

  /* ── JSON-LD Product schema for Google/Naver Shopping ── */
  const productJsonLd = product ? {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: pageDesc,
    image: product.image || `${BASE_URL}/og-image.png`,
    brand: { "@type": "Brand", name: product.brand || "일광오피스" },
    sku: product.sku || `IK-${product.id || "0"}`,
    category: product.category || "복사기",
    offers: {
      "@type": "AggregateOffer",
      url: canonical,
      priceCurrency: "KRW",
      lowPrice: product.rentalPrice || product.price || 30000,
      highPrice: product.price || 0,
      offerCount: 2,
      availability: "https://schema.org/InStock",
      seller: { "@type": "Organization", name: SITE_NAME, url: BASE_URL },
      offers: [
        ...(product.rentalPrice > 0 ? [{
          "@type": "Offer",
          name: "월 렌탈",
          price: product.rentalPrice,
          priceCurrency: "KRW",
          priceSpecification: { "@type": "UnitPriceSpecification", price: product.rentalPrice, priceCurrency: "KRW", unitText: "월", referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitCode: "MON" } },
          availability: "https://schema.org/InStock",
        }] : []),
        ...(product.price > 0 ? [{
          "@type": "Offer",
          name: "구매",
          price: product.price,
          priceCurrency: "KRW",
          availability: "https://schema.org/InStock",
        }] : []),
      ],
    },
    ...(product.specs ? { additionalProperty: Object.entries(product.specs).slice(0, 10).map(([k, v]) => ({ "@type": "PropertyValue", name: k, value: v })) } : {}),
  } : null;

  /* ── Breadcrumb JSON-LD ── */
  const breadcrumbJsonLd = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((b, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: b.name,
      item: b.url ? `${BASE_URL}${b.url}` : undefined,
    })),
  } : null;

  /* ── Service + LocalBusiness JSON-LD (지역 렌탈 — areaServed 로컬 신호) ── */
  const serviceJsonLd = areaServed ? {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "복합기·복사기 렌탈",
    name: pageTitle,
    description: pageDesc,
    url: canonical,
    areaServed: { "@type": "AdministrativeArea", name: areaServed },
    provider: {
      "@type": "LocalBusiness",
      name: SITE_NAME,
      telephone: "1600-7542",
      url: BASE_URL,
      priceRange: "₩₩",
      areaServed: { "@type": "AdministrativeArea", name: areaServed },
    },
  } : null;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta name="keywords" content={pageKeywords} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="ko_KR" />
      {product ? <meta property="og:type" content="product" /> : <meta property="og:type" content="website" />}
      {product && product.image && <meta property="og:image" content={product.image} />}
      {product && <meta property="product:price:amount" content={String(product.price || 0)} />}
      {product && <meta property="product:price:currency" content="KRW" />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDesc} />

      {/* JSON-LD Product structured data */}
      {productJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(productJsonLd)}
        </script>
      )}

      {/* JSON-LD Breadcrumbs */}
      {breadcrumbJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd)}
        </script>
      )}

      {/* JSON-LD Service (지역 렌탈 areaServed 로컬 신호) */}
      {serviceJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(serviceJsonLd)}
        </script>
      )}
    </Helmet>
  );
}
